import React, { useEffect, useRef, useState } from "react";
import { getApiClient } from "../../libs/axiosInstance";
import { loadAuth } from "../../libs/authMgmt";
import { getCurrencySymbol, getCurrentData } from "../../libs/countryData";

export default function LeaderboardCard() {
  const axios = useRef(null);
  if (!axios.current) axios.current = getApiClient();

  const currentUserId = loadAuth("user")?._id;
  const [metric, setMetric] = useState("count"); // "count" | "items" | "amount"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // daily LS key
  const todayKey = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `lb_notice_${y}${m}${day}`;
  })();
  const monthYearKey = (() => {
    const d = new Date();
    // Get the full year
    const y = d.getFullYear();
    // Get the month (0-indexed, so add 1) and pad it to 2 digits
    const m = String(d.getMonth() + 1).padStart(2, "0");

    // The key format is now 'lb_notice_YYYYMM'
    return `lb_notice_${y}${m}_${metric}`;
  })();

  // fetch on metric change
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await axios.current.get(
          `/api/stats/leaderboard?metric=${metric}&limit=20`
        );
        if (cancel) return;
        const list = Array.isArray(resp.data?.rows) ? resp.data.rows : [];
        setRows(list);

        // SweetAlert (Top 1-3)
        const mine = list.find(
          (r) =>
            r.cashierId &&
            currentUserId &&
            String(r.cashierId) === String(currentUserId)
        );
        if (mine && mine.rank <= 3 && !localStorage.getItem(monthYearKey)) {
          // Try SweetAlert2 if available, graceful fallback otherwise
          const showCongrats = async () => {
            try {
              const { default: Swal } = await import("sweetalert2"); // works if installed; if not, goes to catch
              const title =
                mine.rank === 1
                  ? "👑 You’re #1!"
                  : mine.rank === 2
                  ? "🥈 You’re #2!"
                  : "🥉 You’re #3!";
              const text =
                mine.rank === 1
                  ? "Legendary performance! Keep the crown shining."
                  : mine.rank === 2
                  ? "Strong work! You’re closing in on the crown."
                  : "Great hustle! On the podium—keep pushing!";
              await Swal.fire({
                title,
                text,
                icon: "success",
                confirmButtonText: "Nice!",
                backdrop: true,
              });
              localStorage.setItem(monthYearKey, "1");
            } catch {
              // fallback
              if (mine.rank <= 5) {
                alert(`Congrats! You're Top ${mine.rank} last month.`);
                localStorage.setItem(monthYearKey, "1");
              }
            }
          };
          if (mine.value > 0) {
            showCongrats();
          }
        }
      } catch (e) {
        setRows([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [metric, monthYearKey, currentUserId]);

  const labelForMetric =
    metric === "amount"
      ? "Total Amount"
      : metric === "items"
      ? "Items Sold"
      : "Sales Count";

  const crown = (rank) => {
    // Font Awesome if present else emoji
    return (
      <span style={{ marginRight: 6 }}>
        {rank <= 3 ? <i className="fa fa-crown" aria-hidden="true" /> : null}
        {rank <= 3 ? " " : ""}
        {rank === 1 ? "👑" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : ""}
      </span>
    );
  };

  return (
    <div className="card leaderboards_cont" style={{ padding: 16 }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h2 className="m-0">Leaderboards (Last Month's Sales)</h2>
        <div className="d-flex" style={{ gap: 8 }}>
          <select
            className="form-select form-select-sm"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
          >
            <option value="count">Sales Count</option>
            <option value="items">Items Sold</option>
            <option value="amount">Total Amount</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-muted">Loading leaderboards…</div>
      ) : rows.length === 0 ? (
        <div className="text-muted">No data.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Rank</th>
                <th>Cashier</th>
                <th className="text-end">{labelForMetric}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={`${r.cashierId}-${r.rank}`}
                  className={
                    String(r.cashierId) === String(currentUserId)
                      ? "table-success"
                      : ""
                  }
                >
                  <td>
                    <b>{r.rank}</b>
                  </td>
                  <td>
                    {r.rank <= 3 ? crown(r.rank) : null}
                    {r.cashierName || r.cashierId || "—"}
                    {String(r.cashierId) === String(currentUserId) ? (
                      <small className="text-muted ms-1">(you)</small>
                    ) : null}
                  </td>
                  <td className="text-end">
                    {metric === "amount"
                      ? getCurrencySymbol() +
                        getCurrentData().normalizeValues(Number(r.value || 0))
                      : Number(r.value || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
